import { Component, Input, Output, EventEmitter, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { ApiService } from '@/apiConnection/ApiService';
import { RolesService } from '@/apiConnection/roles.service';
import { Urls } from '@/apiConnection/urls';
import { Utils } from '@/utils/utils';
import { LoadingComponent } from '@/components/loading/loading.component';
import { USERNAME_PATTERN, USERNAME_PATTERN_MESSAGE } from '@/apiConnection/model/user';

@Component({
  selector: 'app-user-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    CommonModule,
    LoadingComponent,
  ],
  templateUrl: './user-form.component.html',
})
export class UserFormComponent implements OnInit {
  @Input() id = 0;
  @Output() closeFunction = new EventEmitter<void>();
  @Output() acceptFunction = new EventEmitter<any>();

  loading = signal(false);
  saving = signal(false);
  roles: any[] = [];
  usernameErrors = { pattern: USERNAME_PATTERN_MESSAGE };

  form = new FormGroup({
    name:     new FormControl('', [Validators.required]),
    username: new FormControl('', [Validators.pattern(USERNAME_PATTERN)]),
    email:    new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl(''),
    role_id:  new FormControl<number | null>(null, [Validators.required]),
  });

  constructor(private apiService: ApiService, private rolesService: RolesService) {}

  ngOnInit() {
    if (this.id) {
      this.form.get('password')!.clearValidators();
    } else {
      this.form.get('password')!.setValidators([Validators.required, Validators.minLength(6)]);
    }
    this.form.get('password')!.updateValueAndValidity();

    this.rolesService.getMyRoles().subscribe({
      next: (res: any) => {
        this.roles = res.roles ?? [];
        if (this.id) {
          this.loadUser();
        } else {
          this.loading.set(false);
        }
      },
      error: () => (this.loading.set(false)),
    });
  }

  private loadUser() {
    this.apiService.getById(Urls.urls.users, this.id).subscribe({
      next: (res: any) => {
        const u = res.user ?? res;
        this.form.patchValue({ name: u.name, username: u.username, email: u.email, role_id: u.role_id });
        this.loading.set(false);
      },
      error: () => (this.loading.set(false)),
    });
  }

  save() {
    if (this.form.invalid) {
      Utils.notification.alert('Completa todos los campos obligatorios');
      return;
    }
    this.saving.set(true);

    const body: any = {
      name:    this.form.value.name,
      username: this.form.value.username?.trim() || null,
      email:   this.form.value.email,
      role_id: this.form.value.role_id,
    };
    if (this.form.value.password) {
      body.password = this.form.value.password;
    }

    this.apiService.save(Urls.urls.users, body, this.id || 0).subscribe({
      next: (res) => {
        Utils.notification.success(this.id ? 'Usuario actualizado' : 'Usuario creado');
        this.acceptFunction.emit(res);
        this.closeFunction.emit();
      },
      error: (err) => {
        this.saving.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(err) || 'Error al guardar el usuario');
      },
      complete: () => (this.saving.set(false)),
    });
  }
}
